<template>
	<div style="width: 100%; height: 100%; position: relative">
		<div id="map"></div>
		<!-- Overlay to block all user interactions until unlock -->
		<div v-if="interactionsLocked" class="map-interaction-blocker" aria-hidden="true" aria-busy="true"
			@touchstart.prevent.stop @touchmove.prevent.stop @touchend.prevent.stop @wheel.prevent.stop
			@mousedown.prevent.stop @mousemove.prevent.stop @mouseup.prevent.stop @click.prevent.stop></div>
		<!-- <div style="position: fixed; color: white; top: 50px">
			{{ locationAccuracy }}
		</div> -->
		<!-- <small v-if="poorAccuracy" class="accuracy-indicator"
			>Poor location accuracy</small -->
		>
		<LoadingComponent v-if="loading" />
		<!-- <div
			style="
				position: fixed;
				z-index: 1000000;
				color: #fff;
				top: 50%;
				left: 50%;
			"
		>
			<div>{{ activePool ? activePool.name : "" }}</div>
			<div>
				{{
					activeDropLocation
						? activeDropLocation.placeName
						: "not in dropzone"
				}}
			</div>
		</div> -->
	</div>
</template>

<script lang="ts">
import { addDropZoneCircles, mountZonesOnMap, selectNearestZones } from "@/utils/geolocationTools";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { DropLocation, DropPool } from "@/types/dropPool";
import LoadingComponent from "@/components/utils/LoadingComponent.vue";
import { VItem } from "@/types/vItem";
import { defineComponent } from "vue";
import { Action, ActionType } from "@/types/customPage";
import { dropPoolStore } from "@/store/pinia_modules/dropPoolStore";
import { vItemStore } from "@/store/pinia_modules/vItemStore";
import { navigationStore } from "@/store/pinia_modules/navigationStore";
import { poolSegmentStore } from "@/store/pinia_modules/poolSegmentStore";
import { authStore } from "@/store/pinia_modules/authStore";
import { campaignStore } from "@/store/pinia_modules/campaignStore";
import { categoryStore } from "@/store/pinia_modules/categoryStore";
import { v4 as uuidv4 } from "uuid";
import { getUserLocation } from "@/utils/geolocationTools";
// import { template } from "@/templates/marker.ts";

const myAccessToken = import.meta.env.VITE_APP_MAPBOX_ACCESS_TOKEN;
const accuracyThreshold = 200;
const dropZoomThreshold = 16.0;

export default defineComponent({
	props: {
		element: {
			type: Object,
			required: true,
		},
	},
	components: { LoadingComponent },
	data() {
		return {
			// loading: false,

			reachedAccuracy: false,
			itemsDropped: false,
			userMarker: null as mapboxgl.Marker | null,
			locations: [] as DropLocation[],
			dropType: "pin",
			map: null as mapboxgl.Map | null,
			markers: [] as mapboxgl.Marker[],
			locationMarkers: {} as { [key: string]: mapboxgl.Marker },
			locationMarkersOnScreen: {} as { [key: string]: mapboxgl.Marker },
			vatMarkers: [] as mapboxgl.Marker[],
			showLocationsAndVats: false,
			selectedMarkerIndex: "",
			arButton: null as HTMLDivElement | null,
			startActiveDropPoolCheck: false,
			initZoom: true,
			vatsAdded: false,
			loadedLocations: false,
			mapStyle: "mapbox://styles/mapbox/streets-v12",
			userManualMapControl: false,
			// mapZooming removed
			canRenderMarkers: false,
			dropAttempted: false,
			introAnimationRan: false,
			fitBoundsOnLoad: false,
			fallbackRevealAllowed: false,
			interactionsLocked: true,
		};
	},

	// beforeMount(): void{
	// this.checkPermissions()
	// },

	mounted(): void {
		this.init();
	},
	beforeUnmount() {
		navigationStore().stopWatchingPosition();
		navigationStore().clearDeniedPermissions();
		// Defensive: clear any legacy dropPool watcher if still running
		const legacyId = dropPoolStore().getWatcher;
		if (legacyId != -1) {
			navigator.geolocation.clearWatch(legacyId);
			dropPoolStore().saveWatcher(-1);
		}
		if (this.map) {
			navigationStore().setPoorAccuracy(false);
			const map = this.map!;
			const safe = (fn: () => void, label: string) => {
				try { fn(); } catch (e) { console.debug(`[Map teardown] ${label} failed`, e); }
			};

			// Detach listeners via loop
			const listeners: Array<[string, any]> = [
				["dragstart", this.dragStartListener],
				["load", this.loadListener],
				["zoom", this.zoomListener],
				["zoomend", this.zoomEndListener],
				["sourcedata", this.sourceDataListener],
			];
			for (const [evt, handler] of listeners) {
				safe(() => map.off(evt as any, handler), `off ${evt}`);
			}

			// Remove layers/sources if present
			const layers = ["clusters", "cluster-count", "radius-circles"];
			for (const id of layers) {
				if (map.getLayer(id)) safe(() => map.removeLayer(id), `removeLayer ${id}`);
			}
			const sources = ["radius-circles", "locations-source"];
			for (const id of sources) {
				if (map.getSource(id)) safe(() => map.removeSource(id), `removeSource ${id}`);
			}

			safe(() => map.remove(), "map.remove()");
			this.map = null;
			const mapElement = document.getElementById("map");
			if (mapElement) {
				mapElement.innerHTML = "";
			}
			this.startActiveDropPoolCheck = false;
		}
	},
	watch: {
		userLocation(to: [number, number], from: [number, number]) {
			if (to && from) {
				dropPoolStore().saveActiveDropPool();
				// Trigger zone check on location change (cached in store)
				if (this.activePool) {
					const lat = to[1];
					const lng = to[0];
					dropPoolStore().zoneCheck({ poolID: this.activePool.id!, latitude: lat, longitude: lng });
				}
				this.moveUserIcon();
			}
		},
		action(to: Action): void {
			if (to) {
				if (to.type == "claim-item") {
					this.claimItem();
				} else if (to.type == "center-map") {
					this.updateUserLocation();
				}
			}
		},

		activePool(to: DropPool, from: DropPool) {
			if (to && from && this.startActiveDropPoolCheck) {
				this.addRemoveDroppedItems(from.id! || "");
			}
			// Render zones when active pool changes
			if (to) {
				this.renderZones();
			}
		},

		activeDropLocation(to: DropLocation) {
			if (this.startActiveDropPoolCheck) {
				if (this.activePool) {
					// this.addRemoveDroppedItems(this.activePool.id!);
					this.removeRadiusCircleLayer();
					if (this.map!.getZoom() >= 14) {
						this.loadRadiusCircleLayer(to);
					}
				} else {
					this.removeItemsFromMap();
				}
			}
		},
	},
	methods: {
		async init() {
			this.hideARBtn();

			// Request location permission using the existing getUserLocation function
			getUserLocation();

			// Check if we already have a valid user location
			if (this.userLocation[0] !== 0 && this.userLocation[1] !== 0) {
				console.log("Already have user location:", this.userLocation);
			}

			navigationStore().clearDeniedPermissions();
			const campaign = campaignStore().getItem;
			if (campaign && campaign.mapStyle && campaign.mapStyle != "") {
				this.mapStyle = campaign.mapStyle;
			}
			this.loadedLocations = false;
			this.startActiveDropPoolCheck = false;
			this.initZoom = true;
			this.vatsAdded = false;

			//clear all the items
			if (vItemStore().items.length > 0) {
				// if there are no pending items, clear the items
				if (vItemStore().getPendingItems.length <= 0) {
					vItemStore().clearItems();
				}
			}

			this.resetDropPoolLocationState();
			// this.startWatchingPosition();

			//Get pools categories and prizes
			if (vItemStore().getPendingItems.length <= 0) {
				await this.getPoolsCategoriesPrizes();
			}
			//Get campaign segments
			// await this.getCampaignSegments();
			//get active dropPool if one exists
			// dropPoolStore().saveActiveDropPool();
			// //Get locations
			this.getLocations();
			// //Check droptype
			this.getDroptype();
			dropPoolStore().setLoading(false);
			this.initMap();
			this.addUserMarker();

			// Robust retry loop (max 4s) to avoid one-shot timing issues on slower devices
			const startedAt = Date.now();
			const check = async () => {
				if (!this.map) {
					setTimeout(check, 300);
					return;
				}
				// Wait a bit for GPS to improve accuracy on slow devices
				if (this.locationAccuracy > 500 && this.locationAccuracy < 10000) {
					await new Promise((resolve) => setTimeout(resolve, 500));
				}
				// Flag accuracy state but don't block drops on it
				navigationStore().setPoorAccuracy(this.locationAccuracy >= accuracyThreshold);
				dropPoolStore().saveActiveDropPool();
				// Render zones for active pool
				this.renderZones();
				// Zone gate before dropping items
				if (this.activePool && !this.dropAttempted) {
					const lat = this.userLocation[1];
					const lng = this.userLocation[0];
					const resp = await dropPoolStore().zoneCheck({ poolID: this.activePool.id!, latitude: lat, longitude: lng, force: true });
					if (resp && resp.data && resp.data.allowed) {
						if (!this.introAnimationRan) {
							try { await this.startIntroCameraAnimation(); } catch (_) { }
						}
						// Drop only after animation settles at/above target zoom
						const dropIfReady = async () => {
							if (this.dropAttempted) return;
							if (!this.map) return;
							if (this.map.getZoom() >= dropZoomThreshold) {
								console.log("Dropping now at zoom:", Number(this.map.getZoom()).toFixed(2));
								this.dropAttempted = true;
								await this.dropItems();
								this.unlockInteractions();
							}
						};
						await dropIfReady();
						if (!this.dropAttempted && this.map) {
							const onMoveEnd = async () => {
								this.map!.off('moveend', onMoveEnd);
								console.log("moveend listener fired. Zoom:", Number(this.map!.getZoom()).toFixed(2));
								await dropIfReady();
							};
							this.map!.on('moveend', onMoveEnd);
						}
						return; // handled
					}
				} else {
					// no active drop pool 
					if (!this.introAnimationRan) {
						try { await this.startIntroCameraAnimation(); } catch (_) { }
					}
					// Drop only after animation settles at/above target zoom
					const dropIfReady = async () => {
						if (this.dropAttempted) return;
						if (!this.map) return;
						if (this.map.getZoom() >= dropZoomThreshold) {
							console.log("Dropping now at zoom:", Number(this.map.getZoom()).toFixed(2));
							this.dropAttempted = true;
							await this.dropItems();
							this.unlockInteractions();
						}
					};
					await dropIfReady();
					if (!this.dropAttempted && this.map) {
						const onMoveEnd = async () => {
							this.map!.off('moveend', onMoveEnd);
							console.log("moveend listener fired. Zoom:", Number(this.map!.getZoom()).toFixed(2));
							await dropIfReady();
						};
						this.map!.on('moveend', onMoveEnd);
					}
					return;
				}

				// Retry up to 4s, then show out-of-zone popup as final feedback
				const elapsed = Date.now() - startedAt;
				if (elapsed < 4000) {
					setTimeout(check, 500);
				} else if (!this.dropAttempted && vItemStore().getPendingItems.length <= 0) {
					// Allow fallback reveal (we're not going to run cinematic)
					this.fallbackRevealAllowed = true;
					this.revealMapSmoothly();
					navigationStore().emitAction({ type: ActionType.OPEN_POPUP, value: "out-of-drop-zone" });
					this.unlockInteractions();
				}
			};
			setTimeout(check, 2000);
		},
		async startIntroCameraAnimation(): Promise<void> {
			if (this.introAnimationRan) return;
			if (!this.map) return;
			if (this.userManualMapControl) return;
			const userCenter = this.userLocation as [number, number];
			if (!userCenter || (userCenter[0] === 0 && userCenter[1] === 0)) return;

			this.introAnimationRan = true;
			const waitForMoveEnd = () => new Promise<void>((resolve) => {
				this.map!.once('moveend', () => resolve());
			});

			try {
				// Ensure style is loaded before animating
				if (!this.map!.isStyleLoaded()) {
					await new Promise<void>((resolve) => this.map!.once('load', () => resolve()));
				}
				// Cinematic "spaceship landing" approach
				// Start position: High altitude, looking straight down, offset from user
				const offsetDistance = 0.9; // ~100km offset in degrees (roughly)
				const startCenter: [number, number] = [
					userCenter[0] + offsetDistance * Math.cos(45 * Math.PI / 180), // Northeast offset
					userCenter[1] + offsetDistance * Math.sin(45 * Math.PI / 180)
				];

				// Set initial camera position (high altitude, straight down) - still hidden
				this.map!.jumpTo({
					center: startCenter,
					zoom: 3, // High altitude
					pitch: 85, // Almost straight down (90 degrees is not allowed in Mapbox)
					bearing: 225 // Looking towards user location
				});

				// Wait a moment for the jump to complete
				await new Promise(resolve => setTimeout(resolve, 200));

				// Pre-register movestart to avoid timing races, then start the cinematic descent
				const moveStartPromise: Promise<void> = new Promise<void>((resolve) => {
					this.map!.once('movestart', () => resolve());
				});

				// Start the cinematic descent
				console.log("Starting flyTo. Current zoom:", Number(this.map!.getZoom()).toFixed(2));
				this.map!.flyTo({
					center: userCenter, // End exactly on user
					zoom: 16.2, // Close zoom
					pitch: 62, // Final angled view
					bearing: 15, // Final bearing
					duration: 5000, // Faster, more cinematic
					essential: true,
					// Dramatic curve that starts fast and slows down (like landing)
					curve: 10.2, // Slightly lower curve for smoother approach
				});

				// Reveal only after movestart, then wait 500ms to avoid pre-animation state
				await moveStartPromise;
				console.log("movestart. Zoom:", Number(this.map!.getZoom()).toFixed(2));
				await new Promise((r) => setTimeout(r, 500));
				this.revealMapSmoothly();
				await waitForMoveEnd();
				console.log("moveend. Zoom:", Number(this.map!.getZoom()).toFixed(2));
			} catch (_) {
				// ignore animation errors
			}
		},
		async ensureZoomAtLeast(minZoom: number, timeoutMs = 10000): Promise<void> {
			if (!this.map) return;
			if (this.map.getZoom() >= minZoom) return;
			return new Promise<void>((resolve) => {
				let resolved = false;
				const done = () => { if (!resolved) { resolved = true; resolve(); } };
				const onMove = () => {
					if (this.map!.getZoom() >= minZoom) {
						this.map!.off('move', onMove);
						done();
					}
				};
				this.map!.on('move', onMove);
				setTimeout(() => {
					this.map!.off('move', onMove);
					done();
				}, timeoutMs);
			});
		},
		renderZones() {
			if (!this.map) return;
			const pool = this.activePool;
			if (!pool || !pool.id) return;
			// Prefer zones from mapdata
			const zonesFromMapData = pool.dropZones || [];
			if (zonesFromMapData.length > 0) {
				const center: [number, number] = [this.userLocation[0], this.userLocation[1]];
				const nearest = selectNearestZones(zonesFromMapData, center, 6);
				mountZonesOnMap(this.map!, "drop-zones", nearest);
				return;
			}
			// Fallback to fetch by pool
			dropPoolStore().fetchDropPoolZones(pool.id!).then((zones) => {
				if (zones && zones.length > 0) {
					const center: [number, number] = [this.userLocation[0], this.userLocation[1]];
					const nearest = selectNearestZones(zones, center, 6);
					mountZonesOnMap(this.map!, "drop-zones", nearest);
				}
			});
		},
		geoJson() {
			let geoJSON = {
				type: "FeatureCollection",
				crs: {
					type: "name",
					properties: { name: "urn:ogc:def:crs:OGC:1.3:CRS84" },
				},
				features: [],
			};
			geoJSON.features = this.locations.map((location) => {
				const id = uuidv4();
				location._id = id;
				return {
					type: "Feature",
					geometry: {
						type: "Point",
						coordinates: [location.longitude, location.latitude],
					},
					properties: {
						id: id,
						title: location.placeName,
						description: location.placeName,
					},
				} as never;
			});
			return geoJSON;
		},
		resetDropPoolLocationState() {
			dropPoolStore().saveLocationAccuracy(100000000);
			dropPoolStore().resetActiveDropPool();
		},
		//Hide AR BTN
		hideARBtn() {
			this.arButton = document.querySelector("#ar");
			if (this.arButton) {
				this.arButton.style.pointerEvents = "none";
				this.arButton.style.opacity = "0";
			}
		},
		showARBtn() {
			if (this.arButton) {
				this.arButton.style.opacity = "1";
				this.arButton.style.pointerEvents = "auto";
			}
		},
		updateUserLocation() {
			this.userManualMapControl = false;
			this.moveUserIcon();
		},

		async getPoolsCategoriesPrizes() {
			await dropPoolStore().getMapData();
		},
		async getCampaignSegments() {
			await poolSegmentStore().getCampaignSegments();
		},
		getLocations() {
			this.locations = dropPoolStore().getDummyVatLocations;
		},
		getDroptype() {
			if (this.activePool) {
				this.dropType = this.activePool.dropType;
			}
		},
		initMap() {
			const mapElement = document.getElementById("map");
			if (mapElement) {
				mapElement.style.opacity = "0";
				mapElement.style.backgroundColor = "#000000"; // Keep black background during loading
				mapboxgl.accessToken = myAccessToken;
				let mapOptions: mapboxgl.MapboxOptions = {
					container: "map",
					style: this.mapStyle,
					center: this.userLocation,
					zoom: 2,
					projection: "globe" as any,
					antialias: true,
				};
				if (!this.map) {
					this.map = new mapboxgl.Map(mapOptions);
					this.map.on("dragstart", this.dragStartListener);
					this.map.on("load", this.loadListener);
					// Lock user interactions until drop completes or we decide otherwise
					this.lockInteractions();

					// Keep map hidden - will be revealed during cinematic animation
					this.map.once('load', () => {
						console.log("Map style loaded - keeping hidden until animation");
						// Fail-safe reveal only after we allow fallback (e.g., out-of-zone) to avoid pre-animation exposure
						setTimeout(() => {
							if (this.fallbackRevealAllowed) {
								this.revealMapSmoothly();
							}
						}, 5000);
					});
				}
			}
		},
		loadListener() {
			// Projection and atmosphere
			try {
				this.map!.setProjection('globe' as any);
				this.map!.setFog({});
				// Simulate ~10am lighting (prefer setLights when available)
				try {
					(this.map as any).setLights?.([
						{ id: 'key', type: 'flat', anchor: 'viewport', position: [1.2, 120, 55], intensity: 0.5 }
					]);
				} catch (_) {
					(this.map as any).setLight?.({ anchor: 'viewport', position: [1.2, 120, 55], intensity: 0.5 });
				}
			} catch (e) {
				console.warn('Projection/fog/light setup failed', e);
			}

			// Terrain (3D landscapes)
			try {
				if (!this.map!.getSource('mapbox-dem')) {
					this.map!.addSource('mapbox-dem', {
						type: 'raster-dem',
						url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
						tileSize: 512,
						maxzoom: 14
					} as any);
				}
				// Mild exaggeration for clarity
				(this.map as any).setTerrain({ source: 'mapbox-dem', exaggeration: 1.0 });
			} catch (e) {
				console.warn('Terrain setup failed', e);
			}

			// 3D buildings beneath labels
			try {
				const styleLayers = (this.map!.getStyle().layers || []) as any[];
				let labelLayerId: string | undefined;
				for (const layer of styleLayers) {
					if (layer.type === 'symbol' && layer.layout && layer.layout['text-field']) {
						labelLayerId = layer.id;
						break;
					}
				}
				if (this.map!.getSource('composite') && !this.map!.getLayer('3d-buildings')) {
					this.map!.addLayer({
						id: '3d-buildings',
						source: 'composite',
						'source-layer': 'building',
						filter: ['==', ['get', 'extrude'], 'true'],
						type: 'fill-extrusion',
						minzoom: 12,
						paint: {
							'fill-extrusion-color': '#aaa',
							'fill-extrusion-height': ['get', 'height'],
							'fill-extrusion-base': ['get', 'min_height'],
							'fill-extrusion-opacity': 0.6
						}
					} as any, labelLayerId);
				}
			} catch (e) {
				console.warn('3D buildings setup failed', e);
			}

			this.map!.addSource("locations-source", {
				type: "geojson",
				data: this.geoJson() as any,
				cluster: true,
				clusterMaxZoom: 14, // Max zoom to cluster points on
				clusterRadius: 50, // Radius of each cluster when clustering points (defaults to 50)
			});

			this.map!.addLayer({
				id: "clusters",
				type: "circle",
				source: "locations-source",
				filter: ["has", "point_count"],
				paint: {
					"circle-color": "rgba(0, 0, 0, 0)",
					"circle-radius": 30,
				},
			}, '3d-buildings');

			this.map!.addLayer({
				id: "cluster-count",
				type: "symbol",
				source: "locations-source",
				filter: ["has", "point_count"],
				layout: {},
			}, 'clusters');

			this.map!.on("zoom", this.zoomListener);
			this.map!.on("zoomend", this.zoomEndListener);

			this.map!.on("sourcedata", this.sourceDataListener);

			// Enable active pool checks as soon as the map is ready
			this.startActiveDropPoolCheck = true;

			// No cinematic zoom on load; using steady initial view
		},
		zoomListener() {
			const zoom = this.map!.getZoom();
			console.log("Map zoom:", Number(zoom).toFixed(2));

			if (zoom >= 14 && !this.vatsAdded) {
				this.vatsAdded = true;
				this.addItemsToMap();
				this.loadRadiusCircleLayer(this.activeDropLocation);
			} else if (zoom < 14 && this.vatsAdded && this.dropType == "gps") {
				this.vatsAdded = false;
				this.removeItemsFromMap();
				this.removeRadiusCircleLayer();
			}
		},
		zoomEndListener() {
			const onClick = async (e: any) => {
				const features = this.map!.queryRenderedFeatures(e.point, {
					layers: ["clusters"],
				});
				const clusterId = features[0].properties!.cluster_id;
				const src = this.map!.getSource("locations-source") as any;

				src.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
					if (err) return;
					const geom = features[0].geometry as any;
					this.map!.easeTo({
						center: geom.coordinates,
						zoom: zoom,
					});
				});
			};
			this.map!.off("click", "clusters", onClick);
			this.map!.on("click", "clusters", onClick);
			if (this.initZoom) {
				this.initZoom = false;
				this.startActiveDropPoolCheck = true;
				setTimeout(() => {
					if (
						this.activePool == null &&
						this.activeDropLocation == null &&
						this.locationAccuracy < accuracyThreshold
					) {
						navigationStore().emitAction({
							type: ActionType.OPEN_POPUP,
							value: "out-of-drop-zone",
						});
					}
				}, 3500);
			}
		},
		sourceDataListener() {
			this.updateLocations();
		},
		dragStartListener(e?: any) {
			if (this.interactionsLocked) {
				if (e && typeof e.preventDefault === 'function') e.preventDefault();
				if (e && e.originalEvent && typeof e.originalEvent.preventDefault === 'function') e.originalEvent.preventDefault();
				return;
			}
			this.userManualMapControl = true;
		},
		updateLocations() {
			const features = this.map!.querySourceFeatures("locations-source");
			const newMarkers = {} as { [key: string]: mapboxgl.Marker };

			for (let i = 0; i < features.length; i++) {
				const feature = features[i];
				if (
					feature.geometry.type == "Point" &&
					!feature.id &&
					feature.properties &&
					feature.properties!.id
				) {
					const loc = this.locations.find(
						(loc: DropLocation) => loc._id == feature.properties!.id
					);
					if (!loc) {
						return;
					}
					const coords = [loc!.longitude, loc!.latitude];
					const id = feature.properties!.id;
					let marker = this.locationMarkers[id];
					if (!marker) {
						marker = this.locationMarkers[id] = new mapboxgl.Marker({
							offset: [0, 0],
							color: loc!.pinColour ?? "#d51919",
						})
							.setLngLat(coords as [number, number])
							.setPopup(
								new mapboxgl.Popup({
									closeButton: true,
								}).setHTML(
									`<strong style="margin-bottom:5px;">${loc!.placeName}</strong>
									${loc!.image
										? `<img style="width:100%; height:100px; object-fit:contain; object-position:center;" src="${loc!.image
										}" />`
										: ""
									}
									${loc!.description ? `<p style="margin:0 5px;">${loc!.description}</p>` : ""}
									${loc!.url
										? `<a style="margin:0 5px;" href="${loc!.url
										}" target="_blank">Website</a>`
										: ""
									}
									`
								)
							);
					}
					newMarkers[id] = marker;

					if (!this.locationMarkersOnScreen[id]) marker.addTo(this.map!);
				}

				//add cluster
				if (feature.id && feature.geometry.type == "Point") {
					const id = feature.id + "_marker";
					const coords = feature.geometry.coordinates;
					let marker = this.locationMarkers[id];
					if (!marker) {
						marker = this.locationMarkers[id] = new mapboxgl.Marker(
							{}
						).setLngLat(coords as [number, number]);
					}
					newMarkers[id] = marker;

					if (!this.locationMarkersOnScreen[id]) marker.addTo(this.map!);
				}
				//add number
				if (feature.id && feature.geometry.type == "Point") {
					const id = feature.id + "_count";
					const coords = feature.geometry.coordinates;
					let marker = this.locationMarkers[id];
					if (!marker) {
						const el = document.createElement("div");
						el.className = "cluster-count";
						el.innerText = feature.properties!.point_count;
						marker = this.locationMarkers[id] = new mapboxgl.Marker(el, {
							offset: [0, -21],
						}).setLngLat(coords as [number, number]);
					}
					newMarkers[id] = marker;

					if (!this.locationMarkersOnScreen[id]) marker.addTo(this.map!);
				}
			}
			for (const id in this.locationMarkersOnScreen) {
				if (!newMarkers[id]) this.locationMarkersOnScreen[id].remove();
			}
			this.locationMarkersOnScreen = newMarkers;
			if (!this.loadedLocations) {
				this.loadedLocations = true;
				// Fallback: reveal the map once locations are ready, but only if allowed
				if (this.fallbackRevealAllowed) {
					this.revealMapSmoothly();
				}
				if (this.locations.length > 0 && this.locationBounds()) {
					this.map!.fitBounds(this.locationBounds(), {
						duration: 0,
						padding: {
							top: 100,
							bottom: 100,
							left: 100,
							right: 100,
						},
					});
				}
			}
		},
		// Backend sends swapped coordinates - fix by using item.latitude as lng, item.longitude as lat
		_getDisplayLngLat(item: { latitude: number; longitude: number }): [number, number] {
			const result = [item.latitude, item.longitude] as [number, number]; // Backend swapped: use lat as lng, lng as lat
			console.log(`🎯 _getDisplayLngLat for item:`, {
				backendLat: item.latitude,
				backendLng: item.longitude,
				mapboxFormat: result,
				userLocation: this.userLocation,
				note: "Backend sends swapped coords - using item.lat as lng, item.lng as lat"
			});
			return result; // Mapbox expects [lng, lat]
		},
		revealMapSmoothly() {
			const el = document.getElementById('map');
			if (el && getComputedStyle(el).opacity === '0') {
				el.style.transition = 'opacity 0.8s ease-out';
				el.style.opacity = '1';
				el.style.backgroundColor = 'transparent';
			}
		},
		lockInteractions() {
			if (!this.map) return;
			this.interactionsLocked = true;
			try { this.map.boxZoom.disable(); } catch (_) { }
			try { this.map.dragPan.disable(); } catch (_) { }
			try { this.map.scrollZoom.disable(); } catch (_) { }
			try { this.map.dragRotate.disable(); } catch (_) { }
			try { this.map.keyboard.disable(); } catch (_) { }
			try { this.map.doubleClickZoom.disable(); } catch (_) { }
			try { (this.map as any).touchZoomRotate?.disable(); } catch (_) { }
		},
		unlockInteractions() {
			if (!this.map) return;
			this.interactionsLocked = false;
			try { this.map.boxZoom.enable(); } catch (_) { }
			try { this.map.dragPan.enable(); } catch (_) { }
			try { this.map.scrollZoom.enable(); } catch (_) { }
			try { this.map.dragRotate.enable(); } catch (_) { }
			try { this.map.keyboard.enable(); } catch (_) { }
			try { this.map.doubleClickZoom.enable(); } catch (_) { }
			try { (this.map as any).touchZoomRotate?.enable(); } catch (_) { }
		},
		addUserMarker(): void {
			const user = document.createElement("div");
			user.style.backgroundColor = `rgba(51, 153, 255, 0.3)`;
			user.style.width = "50px";
			user.style.height = "50px";
			user.style.borderRadius = "50px";
			user.style.animation = "anim 1s infinite alternate";
			user.style.display = "flex";
			user.style.justifyContent = "center";
			user.style.alignItems = "center";
			const elem = document.createElement("div");
			elem.style.backgroundColor = `#3399ff`;
			elem.style.width = "15px";
			elem.style.height = "15px";
			elem.style.borderRadius = "10px";
			elem.style.boxShadow = "2px 2px 3px rgba(0, 0, 0, 0.1) inset";

			user.appendChild(elem);
			if (this.userMarker) {
				this.userMarker.remove();
			}

			this.userMarker = new mapboxgl.Marker({
				draggable: false,
				element: user,
			}).setLngLat(this.userLocation);
			if (this.map) {
				this.userMarker.addTo(this.map);
			}
		},

		async dropItems() {
			// Check if there are any pending items
			const pendingItems = vItemStore().getPendingItems;

			if (pendingItems.length > 0) {
				console.log("Found pending items:", pendingItems);

				// Make sure we have valid user location before updating
				if (this.userLocation[0] === 0 && this.userLocation[1] === 0) {
					console.warn("Cannot update item location: User location not available yet");
					return; // Exit early if we don't have a valid location
				}

				console.log("Updating pending items with location:", this.userLocation);

				// Only update items that don't have coordinates yet
				for (const item of pendingItems) {
					console.log(`🔍 CHECKING PENDING ITEM ${item.id}:`, {
						itemLat: item.latitude,
						itemLng: item.longitude,
						hasValidCoords: !(!item.longitude || !item.latitude || (item.longitude === 0 && item.latitude === 0)),
						userLocation: this.userLocation
					});
					// Only update if item genuinely lacks coordinates
					if (!item.longitude || !item.latitude || (item.longitude === 0 && item.latitude === 0)) {
						console.log(`⚠️ OVERRIDING item ${item.id} with user location:`, this.userLocation);
						vItemStore().updateItemLocation({
							id: item.id!,
							latitude: this.userLocation[1], // Use correct lat from user location
							longitude: this.userLocation[0] // Use correct lng from user location
						});
					} else {
						console.log(`✅ PRESERVING item ${item.id} coordinates:`, { lat: item.latitude, lng: item.longitude });
					}
				}
			} else if (this.activePool) {
				// No pending items, drop new items
				const walletID = authStore().walletID;
				console.log("dropping!!!!!");
				await vItemStore().dropItems({
					walletID: walletID || "",
					segmentID: "null",
					poolID: this.activePool!.id!,
					userLocation: [this.userLocation[1], this.userLocation[0]],
				});
			}
			this.addItemsToMap();
		},
		async addRemoveDroppedItems(poolID: string) {
			await vItemStore().recycleItems({
				walletID: this.walletID!,
				poolID: poolID,
			});
			// Only drop if zoom is at target level (avoid dropping during animation)
			if (!this.map || this.map.getZoom() >= dropZoomThreshold) {
				await this.dropItems();
			}
			this.addItemsToMap();
		},
		moveUserIcon() {
			if (this.map && this.userMarker) {
				this.userMarker.remove();
				this.addUserMarker();
				// No automatic flyTo on user location updates for now
			}
		},
		addItemsToMap(): void {
			// Clear existing markers and symbol layers
			this.clearItemsFromMap();

			// Get all items, including both dropped and pending items
			const allItems = [...this.vItems];

			console.log("🗺️ ADDING ITEMS TO MAP:", allItems.map(i => ({
				id: i.id,
				stat: i.itemStatus,
				lat: i.latitude,
				lng: i.longitude
			})));

			// Filter out items without valid coordinates
			const validItems = allItems.filter(item => {
				console.log(`🔍 VALIDATING ITEM ${item.id}:`, {
					status: item.itemStatus,
					lat: item.latitude,
					lng: item.longitude,
					missingCoords: !item.longitude || !item.latitude || (item.longitude === 0 && item.latitude === 0)
				});
				if (item.itemStatus == "pending") {
					// Only fallback to user location if coordinates are truly missing
					if (!item.longitude || !item.latitude || (item.longitude === 0 && item.latitude === 0)) {
						console.warn(`FALLBACK: Item ${item.id} missing coordinates, using user location:`, item);
						item.latitude = this.userLocation[1]; // Correct lat
						item.longitude = this.userLocation[0]; // Correct lng
						return true;
					}
				}
				return true;
			});

			console.log(`Found ${validItems.length} items with valid coordinates`);

			if (validItems.length === 0) {
				return;
			}

			// Load marker images and create GeoJSON source data for symbol layer
			const imagePromises: Promise<void>[] = [];
			const vitemsGeoJSON = {
				type: "FeatureCollection" as const,
				features: validItems.map((item, index) => {
					const coords = this._getDisplayLngLat(item);
					const category = categoryStore().getItemByID(item.categoryID);
					const imageId = `vitem-${index}`;

					console.log(`📍 CREATING GEOJSON FEATURE for item ${item.id}:`, {
						originalCoords: { lat: item.latitude, lng: item.longitude },
						geoJsonCoords: coords,
						status: item.itemStatus,
						markerUrl: item.marker,
						imageId: imageId
					});

					// Try to load the marker image into Mapbox (may fail due to CORS)
					console.log(`ITEM MARKER: ${item.marker}`);
					console.log(`IMAGE ID: ${imageId}`);
					console.log(`MAP HAS IMAGE: ${this.map!.hasImage(imageId)}`);
					if (item.marker && !this.map!.hasImage(imageId)) {
						console.log(`LOADING IMAGE FOR ITEM ${JSON.stringify(item)}`);
						const imagePromise = new Promise<void>((resolve) => {
							this.map!.loadImage(item.marker, (error: any, image: any) => {
								if (error) {
									console.warn(`⚠️ CORS blocked image for ${item.id}, will use HTML marker fallback:`, error);
									// Mark this item for HTML marker fallback
									(item as any)._useFallback = true;
									resolve();
									return;
								}
								if (image && !this.map!.hasImage(imageId)) {
									try {
										this.map!.addImage(imageId, image);
										console.log(`🖼️ Added image ${imageId} to map`);
									} catch (webglError) {
										console.warn(`⚠️ WebGL error adding image for ${item.id}, will use HTML marker fallback:`, webglError);
										(item as any)._useFallback = true;
									}
								}
								resolve();
							});
						});
						imagePromises.push(imagePromise);
					}

					return {
						type: "Feature" as const,
						geometry: {
							type: "Point" as const,
							coordinates: coords
						},
						properties: {
							id: item.id,
							imageId: imageId,
							categoryID: item.categoryID,
							imageSize: category?.imageSize || 32,
							status: item.itemStatus
						}
					};
				})
			};

			// Wait for all images to load before adding the layer
			Promise.all(imagePromises).then(() => {
				// Split items into symbol layer (successful) and HTML marker fallback
				const symbolItems = validItems.filter(item => !(item as any)._useFallback);
				const fallbackItems = validItems.filter(item => (item as any)._useFallback);

				console.log(`🔀 Using symbol layer for ${symbolItems.length} items, HTML fallback for ${fallbackItems.length} items`);

				// Add symbol layer for items that loaded successfully
				if (symbolItems.length > 0) {
					const symbolGeoJSON = {
						...vitemsGeoJSON,
						features: vitemsGeoJSON.features.filter((feature: any) => {
							const item = validItems.find(i => i.id === feature.properties.id);
							return !(item as any)?._useFallback;
						})
					};
					this.addVitemsSymbolLayer(symbolGeoJSON);
				}

				// Add HTML markers for CORS-blocked items
				if (fallbackItems.length > 0) {
					this.addFallbackHtmlMarkers(fallbackItems);
				}
			});

			if (validItems.length > 0) {
				this.showARBtn();
			}
		},
		addVitemsSymbolLayer(vitemsGeoJSON: any): void {
			console.log(`ADDING VITEMS SYMBOL LAYER: ${JSON.stringify(vitemsGeoJSON)}`);
			// Add source if it doesn't exist
			if (!this.map!.getSource('vitems-source')) {
				this.map!.addSource('vitems-source', {
					type: 'geojson',
					data: vitemsGeoJSON
				});
				console.log("🔧 Added vitems-source to map");
			} else {
				// Update existing source
				(this.map!.getSource('vitems-source') as mapboxgl.GeoJSONSource).setData(vitemsGeoJSON);
				console.log("🔄 Updated vitems-source data");
			}

			// Add symbol layer if it doesn't exist
			if (!this.map!.getLayer('vitems-layer')) {
				this.map!.addLayer({
					id: 'vitems-layer',
					type: 'symbol',
					source: 'vitems-source',
					layout: {
						'icon-image': ['get', 'imageId'], // Use the preloaded image ID
						'icon-size': 0.06, // Fixed reasonable size for map markers
						'icon-anchor': 'bottom', // Anchor to bottom for ground positioning
						'icon-allow-overlap': true,
						'icon-ignore-placement': true,
						'icon-pitch-alignment': 'auto', // Align with map pitch
						'icon-rotation-alignment': 'auto' // Align with map rotation
					},
					paint: {
						'icon-opacity': 1
					}
				} as any);

				console.log("🎨 Added vitems symbol layer to map");

				// Add click handler for symbol layer
				this.map!.on('click', 'vitems-layer', (e) => {
					if (e.features && e.features.length > 0) {
						const feature = e.features[0];
						const itemId = feature.properties?.id;
						if (itemId) {
							console.log(`🖱️ Clicked on vitem: ${itemId}`);
							this.selectedMarkerIndex = itemId;
							navigationStore().emitAction({
								type: ActionType.OPEN_POPUP,
								value: "claim-item",
							});
						}
					}
				});

				// Change cursor on hover
				this.map!.on('mouseenter', 'vitems-layer', () => {
					this.map!.getCanvas().style.cursor = 'pointer';
				});

				this.map!.on('mouseleave', 'vitems-layer', () => {
					this.map!.getCanvas().style.cursor = '';
				});
			}

			console.log("✅ Successfully added vitems as symbol layer");
		},
		addFallbackHtmlMarkers(items: any[]): void {
			console.log(`🔄 Adding ${items.length} HTML marker fallbacks`);

			for (let i = 0; i < items.length; i++) {
				const item = items[i];
				const category = categoryStore().getItemByID(item.categoryID);

				if (!category) {
					console.error("Category not found for fallback item:", item);
					continue;
				}

				console.log(`📍 CREATING FALLBACK HTML MARKER for item ${item.id}:`, {
					originalCoords: { lat: item.latitude, lng: item.longitude },
					displayCoords: this._getDisplayLngLat(item),
					status: item.itemStatus
				});

				const elem = document.createElement("div");
				elem.setAttribute("id", item.id!);
				elem.style.backgroundImage = `url('${item.marker}')`;
				elem.style.backgroundSize = "contain";
				elem.style.backgroundPosition = "center";
				elem.style.backgroundRepeat = "no-repeat";
				elem.style.width = `${category.imageSize}px`;
				elem.style.height = `${category.imageSize}px`;
				elem.style.zIndex = "1000";
				elem.style.pointerEvents = "auto";
				elem.classList.add("vat-marker");

				const markerCoords = this._getDisplayLngLat(item);
				const marker = new mapboxgl.Marker({
					draggable: false,
					element: elem,
					anchor: 'bottom', // Anchor to bottom so marker sits on ground
					pitchAlignment: 'auto', // Align with map pitch  
					rotationAlignment: 'auto' // Align with map rotation
				}).setLngLat(markerCoords);

				// Add to map and store reference
				marker.addTo(this.map!);
				this.markers.push(marker);

				// Add click handler
				elem.addEventListener("click", () => {
					this.selectedMarkerIndex = item.id!;
					navigationStore().emitAction({
						type: ActionType.OPEN_POPUP,
						value: "claim-item",
					});
				});

				console.log(`🎯 Added fallback marker for ${item.id}`);
			}
		},
		removeItemsFromMap(): void {
			this.clearItemsFromMap();
		},
		clearItemsFromMap(): void {
			// Remove HTML markers (legacy)
			if (this.markers.length > 0) {
				for (let i = this.markers.length - 1; i >= 0; i--) {
					this.markers[i].remove();
					this.markers.splice(i, 1);
				}
				console.log("🧹 Cleared HTML markers");
			}

			// Remove symbol layer and source
			if (this.map) {
				if (this.map.getLayer('vitems-layer')) {
					this.map.removeLayer('vitems-layer');
					console.log("🧹 Removed vitems symbol layer");
				}
				if (this.map.getSource('vitems-source')) {
					this.map.removeSource('vitems-source');
					console.log("🧹 Removed vitems source");
				}
			}
		},
		removeClaimedItem(itemId: string): void {
			// Remove from HTML markers first (fallback markers)
			for (let i = this.markers.length - 1; i >= 0; i--) {
				const markerElement = this.markers[i].getElement();
				if (markerElement && markerElement.id === itemId) {
					this.markers[i].remove();
					this.markers.splice(i, 1);
					console.log(`🎯 Removed HTML marker for claimed item: ${itemId}`);
					break;
				}
			}

			// Update symbol layer source data by filtering out the claimed item
			const source = this.map?.getSource('vitems-source') as mapboxgl.GeoJSONSource;
			if (source) {
				// Get current items (excluding the claimed one)
				const remainingItems = this.vItems.filter(item => item.id !== itemId);

				// Recreate GeoJSON with remaining items
				const updatedGeoJSON = {
					type: "FeatureCollection" as const,
					features: remainingItems.map((item, index) => {
						const coords = this._getDisplayLngLat(item);
						const category = categoryStore().getItemByID(item.categoryID);
						const imageId = `vitem-${index}`;

						return {
							type: "Feature" as const,
							geometry: {
								type: "Point" as const,
								coordinates: coords
							},
							properties: {
								id: item.id,
								imageId: imageId,
								categoryID: item.categoryID,
								imageSize: category?.imageSize || 32,
								status: item.itemStatus
							}
						};
					})
				};

				// Update the source with filtered data
				source.setData(updatedGeoJSON);
				console.log(`🎯 Updated symbol layer, removed claimed item: ${itemId}`);
			}
		},
		loadRadiusCircleLayer(location: DropLocation | null) {
			if (!this.map!.getLayer("radius-circles") && location) {
				const circles: {
					latitude: number;
					longitude: number;
					radius: number;
				}[] = [];

				circles.push({
					latitude: location.latitude,
					longitude: location.longitude,
					radius: location.radius! / 1000,
				});

				this.map!.addLayer({
					id: "radius-circles",
					type: "fill",
					source: addDropZoneCircles(circles) as mapboxgl.AnySourceData,
					layout: {},
					paint: {
						"fill-color": "#2EB3FF",
						"fill-opacity": 0.2,
					},
				});
			}
		},
		removeRadiusCircleLayer() {
			if (this.map!.getLayer("radius-circles")) {
				this.map!.removeLayer("radius-circles");
				this.map!.removeSource("radius-circles");
			}
		},
		//Helper functions
		locationBounds(): mapboxgl.LngLatBounds {
			// const bounds: [number, number][] = [];
			const bounds = new mapboxgl.LngLatBounds();

			for (let i = 0; i < this.locations.length; i++) {
				bounds.extend([
					this.locations[i].longitude,
					this.locations[i].latitude,
				]);
			}
			console.log(bounds);
			return bounds;
		},


		async claimItem() {
			// selectedMarkerIndex is now the item ID (set in symbol layer click handler)
			const id = this.selectedMarkerIndex;
			dropPoolStore().setLoading(true);

			try {
				const pendingItem = vItemStore().getPendingItems.find(
					(item) => item.id == id
				);
				await vItemStore().claimItem(id);

				// Remove only the claimed item without affecting others
				this.removeClaimedItem(id);

				// Clear selection
				this.selectedMarkerIndex = "";

				dropPoolStore().setLoading(false);
				// check selected vitem skipToGameplay
				const vitem = vItemStore().items.find(
					(item) => item.id == id
				);

				if (pendingItem && pendingItem.isDeepLink) {
					vItemStore().selectItem(id);
					navigationStore().emitAction({
						type: ActionType.GO_TO_PAGE,
						value: "prize/" + id,
					});
					return;
				}
				// if skipToGameplay is true, skip to the vault page
				// this will be used in the vault to skip to the prize page rendering
				if (vitem && vitem.skipToGameplay) {
					vItemStore().selectItem(id);
					navigationStore().emitAction({
						type: ActionType.GO_TO_PAGE,
						value: "prize/" + id,
					});
					return;
				}
				if (this.vItems.length == 0) {
					navigationStore().emitAction({
						type: ActionType.GO_TO_PAGE,
						value: "vault",
					});
				}
			} catch (error) {
				console.error(error);
				this.selectedMarkerIndex = "";
				dropPoolStore().setLoading(false);
			}
		},
	},
	computed: {
		//Store getters
		activePool(): DropPool | null {
			return dropPoolStore().getActivePool;
		},
		loading(): boolean {
			return dropPoolStore().getLoading;
		},
		walletID(): string | null {
			return authStore().getWalletID;
		},
		activeDropLocation(): DropLocation | null {
			return dropPoolStore().getActiveDropLocation;
		},
		vItems(): VItem[] {
			// Get all items
			const droppedItems = vItemStore().getDroppedItems || [];
			const pendingItems = vItemStore().getPendingItems || [];

			// Combine both types of items
			const allItems = [...droppedItems, ...pendingItems];

			// Log the items for debugging
			console.log("vItems computed property:", {
				droppedItems: droppedItems.length,
				pendingItems: pendingItems.length,
				allItems: allItems.length
			});

			return allItems;
		},
		action(): Action {
			return navigationStore().getAction;
		},
		userLocation(): [number, number] {
			return dropPoolStore().getUserLocation;
		},
		locationAccuracy(): number {
			return dropPoolStore().getLocationAccuracy;
		},
		watcher(): number {
			return dropPoolStore().getWatcher;
		},
		poorAccuracy(): boolean {
			return navigationStore().getPoorAccuracy;
		},
	},
});
</script>

<style lang="scss">
#map {
	position: relative;
	height: 100%;
	opacity: 0;
}

.map-interaction-blocker {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 10000;
	background: transparent;
	pointer-events: all;
	/* Block all gestures and mouse interactions on all devices */
	touch-action: none;
	/* Block all mouse events on desktop */
	cursor: not-allowed;
	user-select: none;
}

.show-map {
	opacity: 1;
}

.drop-button {
	position: absolute;
	right: 15px;
	top: 15px;
}

.dev-info {
	position: fixed;
	top: 0;
	left: 0;
	color: #fff;
}

@keyframes anim {
	from {
		background-color: rgba(51, 153, 255, 0.3);
	}

	to {
		background-color: rgba(51, 153, 255, 0.1);
	}
}

.cluster-count {
	color: #000;
	display: flex;
	justify-content: center;
	align-items: center;
	border-radius: 100px;
	font-size: 11px;
	width: 20px;
	height: 20px;
	background-color: #fff;
}

.accuracy-indicator {
	position: absolute;
	z-index: 1;
	top: 10px;
	left: 50%;
	transform: translate(-50%, 0);
	background-color: #ff00007b;
	color: #fff;
	padding: 5px 10px;
	border-radius: 100px;
	box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
}

.location-loading-overlay {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 9999;
	pointer-events: none;
}

.location-loading-text {
	color: #000000;
	font-size: 18px;
	font-weight: 500;
	text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}
</style>
